import type { QuotaUsage } from "@/types";

export type IngestCardState = "onPace" | "overPace" | "nearCap";

export interface IngestBarSegment {
  widthPct: number;
  className: string;
}

export interface IngestCardModel {
  state: IngestCardState;
  usagePct: number;
  markerPct: number;
  segments: IngestBarSegment[];
  usedDisplay: string;
  allowanceDisplay: string;
  uploadedDisplay: string;
  streamedDisplay: string;
  remainingDisplay: string;
  dayOfPeriod: number;
  daysInPeriod: number;
}

const DAY_MS = 86_400_000;

const GREEN_USAGE = "bg-[#22c55e]";
const AMBER_FIRST = "bg-[#fbbf24]";
const AMBER_SECOND = "bg-[#f59e0b]";
const RED_FIRST = "bg-[#f87171]";
const RED_SECOND = "bg-[#ef4444]";
const MID_GRAY = "bg-[#cbd5e1]";
const TRACK = "bg-[#e2e8f0]";
// drop zero-width/float-noise slivers
const MIN_SEGMENT_PCT = 0.01;

export function formatHours(seconds: number): string {
  const hours = seconds / 3600;
  const rounded = Math.round(hours * 10) / 10;
  if (rounded === 0) return seconds > 0 ? "< 0.1 h" : "0 h";
  const fixed = rounded.toFixed(1);
  return `${fixed.endsWith(".0") ? fixed.slice(0, -2) : fixed} h`;
}

export function buildIngestCardModel(
  quota: QuotaUsage,
  now: Date = new Date(),
): IngestCardModel {
  const { period, planLimits, ingest } = quota;
  const usedSeconds = ingest.uploaded.seconds + ingest.streamed.seconds;
  const allowance = planLimits.monthlyIngestSeconds;

  const startMs = new Date(period.start).getTime();
  const endMs = new Date(period.end).getTime();
  const nowMs = now.getTime();

  const markerPct =
    endMs > startMs
      ? clamp(((nowMs - startMs) / (endMs - startMs)) * 100, 0, 100)
      : 0;
  // Exact integer check that at least 1/3 of the period has elapsed
  // (fp-safe where markerPct >= 100/3 is not).
  const pastPaceWarningFloor = (nowMs - startMs) * 3 >= endMs - startMs;
  const rawUsagePct = allowance > 0 ? (usedSeconds / allowance) * 100 : 0;
  const fillPct = clamp(rawUsagePct, 0, 100);

  const state: IngestCardState =
    rawUsagePct >= quota.warningThreshold
      ? "nearCap"
      : rawUsagePct > markerPct && pastPaceWarningFloor
        ? "overPace"
        : "onPace";

  const segments: IngestBarSegment[] = [];
  if (state === "onPace" || (state === "nearCap" && rawUsagePct <= markerPct)) {
    pushSegment(segments, fillPct, state === "onPace" ? GREEN_USAGE : RED_FIRST);
    pushSegment(segments, markerPct - fillPct, MID_GRAY);
    pushSegment(segments, 100 - Math.max(markerPct, fillPct), TRACK);
  } else {
    pushSegment(segments, markerPct, state === "overPace" ? AMBER_FIRST : RED_FIRST);
    pushSegment(segments, fillPct - markerPct, state === "overPace" ? AMBER_SECOND : RED_SECOND);
    pushSegment(segments, 100 - fillPct, TRACK);
  }

  return {
    state,
    usagePct: Math.floor(rawUsagePct),
    markerPct,
    segments,
    usedDisplay: formatHours(usedSeconds),
    allowanceDisplay: formatHours(allowance),
    uploadedDisplay: formatHours(ingest.uploaded.seconds),
    streamedDisplay: formatHours(ingest.streamed.seconds),
    remainingDisplay: formatHours(Math.max(0, allowance - usedSeconds)),
    dayOfPeriod: Math.floor(Math.max(0, nowMs - startMs) / DAY_MS) + 1,
    daysInPeriod: Math.max(1, Math.round((endMs - startMs) / DAY_MS)),
  };
}

function pushSegment(
  segments: IngestBarSegment[],
  widthPct: number,
  className: string,
): void {
  if (widthPct > MIN_SEGMENT_PCT) {
    segments.push({ widthPct, className });
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
